import PasswordInput from '../components/PasswordInput';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon, ArrowRightOnRectangleIcon, MagnifyingGlassIcon,
  PhoneIcon, EnvelopeIcon, MapPinIcon, PlusIcon, UserCircleIcon,
  CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon,
  XMarkIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';

const fmt      = n => (n||0).toLocaleString('en-PK');
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : 'Never';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ob * { box-sizing:border-box; }
  .ob { font-family:'Sora',sans-serif; color:#1a1d2e; animation:obFade .4s ease both; }
  @keyframes obFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ob-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ob-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .ob-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .ob-search-wrap { position:relative; margin-bottom:1.5rem; }
  .ob-search-wrap svg { position:absolute; left:14px; top:50%; transform:translateY(-50%); width:16px; height:16px; stroke:#9094a8; stroke-width:2; pointer-events:none; }
  .ob-search { font-family:'Sora',sans-serif; font-size:.84rem; width:100%; max-width:380px; padding:10px 14px 10px 40px; border:1.5px solid #e4e7f0; border-radius:12px; background:white; color:#1a1d2e; outline:none; }
  .ob-search:focus { border-color:#7c3aed; }
  .ob-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(340px,1fr)); gap:1.2rem; }
  .ob-card { background:white; border-radius:18px; border:1px solid #e8ebf4; overflow:hidden; transition:box-shadow .18s; animation:obFade .4s ease both; }
  .ob-card:hover { box-shadow:0 6px 24px rgba(124,58,237,.08); }
  .ob-card-body { padding:1.4rem; }
  .ob-card-top { display:flex; align-items:flex-start; gap:14px; margin-bottom:1.2rem; }
  .ob-logo { width:52px; height:52px; border-radius:14px; background:linear-gradient(135deg,#7c3aed,#4f46e5); display:flex; align-items:center; justify-content:center; font-size:1.1rem; font-weight:700; color:white; flex-shrink:0; overflow:hidden; }
  .ob-logo img { width:100%; height:100%; object-fit:cover; }
  .ob-name { font-size:1rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .ob-meta { font-size:.72rem; color:#9094a8; display:flex; align-items:center; gap:5px; margin-bottom:2px; }
  .ob-meta svg { width:11px; height:11px; flex-shrink:0; }
  .ob-stats { display:grid; grid-template-columns:1fr 1fr; gap:.7rem; margin-bottom:1.2rem; }
  .ob-stat { background:#f8f9fc; border-radius:10px; padding:.7rem .9rem; }
  .ob-stat-label { font-size:.64rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .ob-stat-val   { font-size:1.1rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .ob-actions { display:flex; gap:8px; flex-wrap:wrap; }
  .ob-enter { flex:1; display:flex; align-items:center; justify-content:center; gap:7px; padding:9px; border:none; border-radius:10px; background:linear-gradient(135deg,#7c3aed,#4f46e5); color:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:700; cursor:pointer; transition:all .18s; }
  .ob-enter:hover { box-shadow:0 4px 14px rgba(124,58,237,.35); transform:translateY(-1px); }
  .ob-enter:disabled { opacity:.5; cursor:not-allowed; transform:none; }
  .ob-enter svg { width:15px; height:15px; stroke-width:2; }
  .ob-admins-btn { display:flex; align-items:center; gap:6px; padding:9px 14px; border:1.5px solid #e8ebf4; border-radius:10px; background:white; color:#6b7194; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; }
  .ob-admins-btn:hover { border-color:#7c3aed; color:#7c3aed; background:#faf8ff; }
  .ob-admins-btn svg { width:14px; height:14px; stroke-width:2; }
  /* Admins panel */
  .ob-admins { border-top:1px solid #f0f2f8; padding:1rem 1.4rem; background:#fafbff; }
  .ob-admins-title { font-size:.76rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; margin-bottom:.8rem; }
  .ob-admin-row { display:flex; align-items:center; gap:10px; padding:8px 10px; background:white; border-radius:10px; border:1px solid #e8ebf4; margin-bottom:6px; }
  .ob-admin-avatar { width:32px; height:32px; border-radius:9px; background:linear-gradient(135deg,#7c3aed,#4f46e5); display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; color:white; flex-shrink:0; }
  .ob-admin-avatar.inactive { background:#e4e7f0; }
  .ob-admin-name  { font-size:.8rem; font-weight:600; color:#0f1123; }
  .ob-admin-email { font-size:.68rem; color:#9094a8; }
  .ob-admin-last  { font-size:.65rem; color:#b0b5cc; margin-top:1px; }
  .pill-active   { font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .pill-inactive { font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; background:#fef2f2; color:#ef4444; }
  .ob-toggle { padding:5px 10px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.72rem; font-weight:600; cursor:pointer; transition:all .15s; flex-shrink:0; }
  .ob-toggle.deactivate { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .ob-toggle.deactivate:hover { background:#ef4444; color:white; }
  .ob-toggle.activate   { background:#ecfdf5; color:#10c98e; border:1px solid #a7f3d0; }
  .ob-toggle.activate:hover   { background:#10c98e; color:white; }
  .ob-toggle:disabled { opacity:.5; cursor:not-allowed; }
  .ob-admins-loading { font-size:.76rem; color:#9094a8; padding:.5rem; }
  /* Add admin button */
  .ob-add-admin { display:flex; align-items:center; gap:6px; padding:6px 12px; border:1.5px dashed #c4b5fd; border-radius:9px; background:transparent; color:#7c3aed; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; transition:all .15s; margin-top:4px; }
  .ob-add-admin:hover { background:#faf8ff; border-color:#7c3aed; }
  .ob-add-admin svg { width:13px; height:13px; }
  /* Add admin inline form */
  .ob-admin-form { background:white; border:1.5px solid #ddd6fe; border-radius:10px; padding:12px; margin-top:8px; }
  .ob-admin-form input { font-family:'Sora',sans-serif; font-size:.78rem; padding:7px 10px; border:1.5px solid #e0e4f0; border-radius:8px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; margin-bottom:7px; }
  .ob-admin-form input:focus { border-color:#7c3aed; background:white; }
  .ob-admin-form-btns { display:flex; gap:7px; }
  .ob-admin-form-btns button { flex:1; padding:7px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; }
  /* Empty + loading */
  .ob-empty { text-align:center; padding:4rem 2rem; }
  .ob-empty-icon { width:60px; height:60px; border-radius:16px; background:#f5f3ff; color:#7c3aed; display:flex; align-items:center; justify-content:center; margin:0 auto 14px; }
  .ob-empty-icon svg { width:28px; height:28px; stroke-width:1.6; }
  .ob-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .ob-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#7c3aed; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  .btn-add { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; background:#7c3aed; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-add:hover { background:#6d28d9; }
  .btn-add svg { width:15px; height:15px; stroke-width:2; }
  .ob-entering { position:fixed; inset:0; background:rgba(30,27,75,.85); backdrop-filter:blur(8px); display:flex; flex-direction:column; align-items:center; justify-content:center; z-index:9999; gap:16px; }
  .ob-entering-spin { width:36px; height:36px; border:3px solid rgba(255,255,255,.2); border-top-color:white; border-radius:50%; animation:spin .7s linear infinite; }
  .ob-entering-text { color:white; font-family:'Sora',sans-serif; font-size:1rem; font-weight:600; }
  @media (max-width:768px) { .ob-grid { grid-template-columns:1fr; } }
`;

const getInitials = n => n?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'S';

export default function OwnerBranches() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [branches,     setBranches]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [entering,     setEntering]     = useState(null);
  const [expanded,     setExpanded]     = useState({}); // { schoolId: bool }
  const [admins,       setAdmins]       = useState({}); // { schoolId: [] }
  const [adminsLoading,setAdminsLoading]= useState({});
  const [toggling,     setToggling]     = useState({}); // { adminId: bool }
  const [addAdmin,     setAddAdmin]     = useState({}); // { schoolId: bool }
  const [addForm,      setAddForm]      = useState({});  // { schoolId: {name,email,password} }
  const [addSaving,    setAddSaving]    = useState({});
  const [checkinOpen,    setCheckinOpen]    = useState({}); // { schoolId: bool }
  const [checkinPerm,    setCheckinPerm]    = useState({}); // { schoolId: {checkinOwnerAllowed, checkinAdminEnabled} }
  const [checkinLoading, setCheckinLoading] = useState({});
  const [checkinSaving,  setCheckinSaving]  = useState({});

  useEffect(() => {
    api.get('/owner/schools')
      .then(r => setBranches(r.data || []))
      .catch(() => toast.error('Failed to load branches'))
      .finally(() => setLoading(false));
  }, []);

  const handleEnter = async (branch) => {
    setEntering(branch.name);
    try {
      const { data } = await api.post(`/owner/enter/${branch._id}`);
      localStorage.setItem('ownerToken', localStorage.getItem('token') || '');
      await loginWithToken(data.token, data.user);
      navigate('/dashboard');
      toast.success(`Entered ${branch.name} as Admin`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to enter branch');
      setEntering(null);
    }
  };

  const toggleAdmins = async (schoolId) => {
    const nowOpen = !expanded[schoolId];
    setExpanded(p => ({ ...p, [schoolId]: nowOpen }));
    if (nowOpen && !admins[schoolId]) {
      setAdminsLoading(p => ({ ...p, [schoolId]: true }));
      try {
        const { data } = await api.get(`/owner/branches/${schoolId}/admins`);
        setAdmins(p => ({ ...p, [schoolId]: data }));
      } catch { toast.error('Failed to load admins'); }
      finally { setAdminsLoading(p => ({ ...p, [schoolId]: false })); }
    }
  };

  const toggleAdmin = async (schoolId, adminId, activate) => {
    setToggling(p => ({ ...p, [adminId]: true }));
    try {
      const action = activate ? 'activate' : 'deactivate';
      await api.put(`/owner/branches/${schoolId}/admins/${adminId}/${action}`);
      setAdmins(p => ({
        ...p,
        [schoolId]: p[schoolId].map(a =>
          a._id === adminId ? { ...a, isActive: activate } : a
        ),
      }));
      toast.success(`Admin ${activate ? 'activated' : 'deactivated'}`);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setToggling(p => ({ ...p, [adminId]: false })); }
  };

  const toggleCheckinPanel = async (schoolId) => {
    const nowOpen = !checkinOpen[schoolId];
    setCheckinOpen(p => ({ ...p, [schoolId]: nowOpen }));
    if (nowOpen && !checkinPerm[schoolId]) {
      setCheckinLoading(p => ({ ...p, [schoolId]: true }));
      try {
        const { data } = await api.get(`/owner/branches/${schoolId}/checkin-permission`);
        setCheckinPerm(p => ({ ...p, [schoolId]: data }));
      } catch { toast.error('Failed to load check-in permission'); }
      finally { setCheckinLoading(p => ({ ...p, [schoolId]: false })); }
    }
  };

  const setCheckinAllowed = async (schoolId, allowed) => {
    setCheckinSaving(p => ({ ...p, [schoolId]: true }));
    try {
      await api.put(`/owner/branches/${schoolId}/checkin-permission`, { allowed });
      setCheckinPerm(p => ({ ...p, [schoolId]: { checkinOwnerAllowed: allowed, checkinAdminEnabled: allowed ? p[schoolId]?.checkinAdminEnabled : false } }));
      toast.success(allowed ? 'Branch admin can now enable self check-in' : 'Self check-in permission revoked');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setCheckinSaving(p => ({ ...p, [schoolId]: false })); }
  };

  const openAddAdmin = (schoolId) => {
    setAddAdmin(p => ({ ...p, [schoolId]: true }));
    setAddForm(p => ({ ...p, [schoolId]: { name:'', email:'', password:'' } }));
  };

  const handleAddAdmin = async (schoolId) => {
    const f = addForm[schoolId] || {};
    if (!f.email || !f.password) return toast.error('Email and password required');
    setAddSaving(p => ({ ...p, [schoolId]: true }));
    try {
      // Use owner/schools endpoint to add admin to existing school
      const { data } = await api.post('/owner/schools/add-admin', {
        schoolId, name: f.name, email: f.email, password: f.password,
      });
      setAdmins(p => ({ ...p, [schoolId]: [...(p[schoolId]||[]), data.admin] }));
      setAddAdmin(p => ({ ...p, [schoolId]: false }));
      toast.success('Admin account created');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setAddSaving(p => ({ ...p, [schoolId]: false })); }
  };

  const filtered = branches.filter(b =>
    !search || b.name?.toLowerCase().includes(search.toLowerCase()) ||
    b.address?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ob"><style>{css}</style>

      {entering && createPortal(
        <div className="ob-entering">
          <div className="ob-entering-spin"/>
          <div className="ob-entering-text">Entering {entering}…</div>
        </div>
      
      , document.body)}

      <div className="ob-hdr">
        <div>
          <div className="ob-title">My Branches</div>
          <div className="ob-sub">{branches.length} school{branches.length!==1?'s':''} — enter any branch or manage admins</div>
        </div>
        <button className="btn-add" onClick={() => navigate('/owner/add-branch')}><PlusIcon />Add Branch</button>
      </div>

      <div className="ob-search-wrap">
        <MagnifyingGlassIcon />
        <input className="ob-search" placeholder="Search branches…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="ob-loading"><div className="ob-spin"/>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="ob-empty">
          <div className="ob-empty-icon"><BuildingOfficeIcon /></div>
          <div style={{fontSize:'.95rem',fontWeight:600,color:'#1a1d2e',marginBottom:5}}>{search?'No branches found':'No branches yet'}</div>
          <div style={{fontSize:'.78rem',color:'#9094a8',marginBottom:20}}>{search?'Try a different search.':'Add your first branch to get started.'}</div>
          {!search && <button className="btn-add" onClick={() => navigate('/owner/add-branch')}><PlusIcon />Add First Branch</button>}
        </div>
      ) : (
        <div className="ob-grid">
          {filtered.map((b, i) => (
            <div key={b._id} className="ob-card" style={{animationDelay:`${i*.06}s`}}>
              <div className="ob-card-body">
                <div className="ob-card-top">
                  <div className="ob-logo">{b.logo ? <img src={b.logo} alt={b.name}/> : getInitials(b.name)}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div className="ob-name">{b.name}</div>
                    {b.address && <div className="ob-meta"><MapPinIcon />{b.address}</div>}
                    {b.phone   && <div className="ob-meta"><PhoneIcon />{b.phone}</div>}
                    {b.email   && <div className="ob-meta"><EnvelopeIcon />{b.email}</div>}
                  </div>
                </div>
                <div className="ob-stats">
                  <div className="ob-stat"><div className="ob-stat-label">Students</div><div className="ob-stat-val">{fmt(b.studentCount)}</div></div>
                  <div className="ob-stat"><div className="ob-stat-label">Teachers</div><div className="ob-stat-val">{fmt(b.teacherCount)}</div></div>
                </div>
                <div className="ob-actions">
                  <button className="ob-enter" onClick={() => handleEnter(b)} disabled={!!entering}>
                    <ArrowRightOnRectangleIcon />Enter Admin Panel
                  </button>
                  <button className="ob-admins-btn" onClick={() => toggleAdmins(b._id)}>
                    <UserCircleIcon />Admins
                    {expanded[b._id] ? <ChevronUpIcon style={{width:12,height:12}}/> : <ChevronDownIcon style={{width:12,height:12}}/>}
                  </button>
                  <button className="ob-admins-btn" onClick={() => toggleCheckinPanel(b._id)}>
                    ⏱️ Check-in
                    {checkinOpen[b._id] ? <ChevronUpIcon style={{width:12,height:12}}/> : <ChevronDownIcon style={{width:12,height:12}}/>}
                  </button>
                  <button className="ob-admins-btn" onClick={() => navigate(`/owner/branches/${b._id}/access`)}>
                    🛡️ Access
                  </button>
                </div>
              </div>

              {/* Check-in permission panel */}
              {checkinOpen[b._id] && (
                <div className="ob-admins">
                  <div className="ob-admins-title">Staff Self Check-in / Check-out</div>
                  {checkinLoading[b._id] ? (
                    <div className="ob-admins-loading"><div className="ob-spin" style={{display:'inline-block',marginRight:8}}/>Loading…</div>
                  ) : (
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:'.8rem',fontWeight:600,color:'#0f1123'}}>Allow this branch's admin to enable it</div>
                        <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:2}}>
                          {checkinPerm[b._id]?.checkinOwnerAllowed
                            ? (checkinPerm[b._id]?.checkinAdminEnabled ? 'Allowed — admin has turned it ON for staff.' : 'Allowed — admin has it turned off for now.')
                            : 'Not allowed — the admin cannot enable self check-in.'}
                        </div>
                      </div>
                      <label style={{position:'relative',display:'inline-block',width:44,height:24,flexShrink:0}}>
                        <input type="checkbox" checked={!!checkinPerm[b._id]?.checkinOwnerAllowed} disabled={checkinSaving[b._id]} style={{opacity:0,width:0,height:0}}
                          onChange={e => setCheckinAllowed(b._id, e.target.checked)} />
                        <span style={{position:'absolute',inset:0,background:checkinPerm[b._id]?.checkinOwnerAllowed?'#10c98e':'#d1d5db',borderRadius:99,transition:'.15s',cursor:checkinSaving[b._id]?'not-allowed':'pointer'}}>
                          <span style={{position:'absolute',height:18,width:18,left:checkinPerm[b._id]?.checkinOwnerAllowed?23:3,top:3,background:'white',borderRadius:'50%',transition:'.15s'}}/>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Admins panel */}
              {expanded[b._id] && (
                <div className="ob-admins">
                  <div className="ob-admins-title">Admin Accounts</div>
                  {adminsLoading[b._id] ? (
                    <div className="ob-admins-loading"><div className="ob-spin" style={{display:'inline-block',marginRight:8}}/>Loading…</div>
                  ) : (admins[b._id]||[]).length === 0 ? (
                    <div style={{fontSize:'.76rem',color:'#9094a8',marginBottom:8}}>No admins found.</div>
                  ) : (
                    (admins[b._id]||[]).map(admin => (
                      <div key={admin._id} className="ob-admin-row">
                        <div className={`ob-admin-avatar${admin.isActive?'':' inactive'}`}>{getInitials(admin.name)}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div className="ob-admin-name">{admin.name}</div>
                          <div className="ob-admin-email">{admin.email}</div>
                          <div className="ob-admin-last">Last login: {fmtDate(admin.lastLogin)}</div>
                        </div>
                        <span className={admin.isActive ? 'pill-active' : 'pill-inactive'}>
                          {admin.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          className={`ob-toggle ${admin.isActive ? 'deactivate' : 'activate'}`}
                          disabled={toggling[admin._id]}
                          onClick={() => toggleAdmin(b._id, admin._id, !admin.isActive)}>
                          {toggling[admin._id] ? '…' : admin.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    ))
                  )}

                  {/* Add admin inline */}
                  {!addAdmin[b._id] ? (
                    <button className="ob-add-admin" onClick={() => openAddAdmin(b._id)}>
                      <PlusCircleIcon />Add Admin Account
                    </button>
                  ) : (
                    <div className="ob-admin-form">
                      <input placeholder="Admin name" value={addForm[b._id]?.name||''} onChange={e => setAddForm(p => ({...p,[b._id]:{...p[b._id],name:e.target.value}}))} />
                      <input type="email" placeholder="Admin email *" value={addForm[b._id]?.email||''} onChange={e => setAddForm(p => ({...p,[b._id]:{...p[b._id],email:e.target.value}}))} />
                      <PasswordInput placeholder="Password (min 6) *" value={addForm[b._id]?.password||''} onChange={e => setAddForm(p => ({...p,[b._id]:{...p[b._id],password:e.target.value}}))} />
                      <div className="ob-admin-form-btns">
                        <button onClick={() => setAddAdmin(p=>({...p,[b._id]:false}))} style={{background:'#f0f2f7',color:'#6b7194'}}>Cancel</button>
                        <button onClick={() => handleAddAdmin(b._id)} disabled={addSaving[b._id]} style={{background:'#7c3aed',color:'white'}}>
                          {addSaving[b._id] ? 'Creating…' : 'Create Admin'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}